/** 告警类别 → 监控卡片指标（无 metricKey 时的兜底） */
const CATEGORY_METRICS = {
  flow: ['stdInstant', 'workInstant', 'stdTotal', 'workTotal'],
  pressure: ['pressure', 'inletPressure', 'outletPressure', 'outletPressure2', 'outletPressure3', 'diffPressure1', 'diffPressure2'],
  temperature: ['temperature'],
  water_quality: ['leakage', 'concentration', 'inletLeak'],
  device_status: ['communicationStatus', 'batteryLevel', 'batteryVoltage', 'signalStrength']
}

/** 执行条件文案优先匹配具体指标 */
const CONDITION_HINTS = [
  { re: /标况累积/, keys: ['stdTotal'] },
  { re: /工况累积/, keys: ['workTotal'] },
  { re: /标况瞬时/, keys: ['stdInstant'] },
  { re: /工况瞬时/, keys: ['workInstant'] },
  { re: /瞬时流量|瞬时/, keys: ['stdInstant', 'workInstant'] },
  { re: /出口压力2|出口压力二/, keys: ['outletPressure2'] },
  { re: /出口压力3|出口压力三/, keys: ['outletPressure3'] },
  { re: /出口压力/, keys: ['outletPressure'] },
  { re: /进口压力/, keys: ['inletPressure'] },
  { re: /差压1|差压一/, keys: ['diffPressure1'] },
  { re: /差压2|差压二/, keys: ['diffPressure2'] },
  { re: /进口泄露|进口泄漏|泄露|泄漏|浓度/, keys: ['leakage', 'concentration', 'inletLeak'] },
  { re: /信号强度|信号/, keys: ['signalStrength'] },
  { re: /电池电量|电量/, keys: ['batteryLevel'] },
  { re: /电池电压|电压/, keys: ['batteryVoltage'] },
  { re: /压力/, keys: ['pressure', 'outletPressure', 'inletPressure', 'outletPressure2', 'outletPressure3', 'diffPressure1', 'diffPressure2'] },
  { re: /温度/, keys: ['temperature'] },
  { re: /流量|累积/, keys: ['stdInstant', 'workInstant', 'stdTotal', 'workTotal'] }
]

function parseNum(v) {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function ruleBounds(rule) {
  const upper = parseNum(rule.thresholdUpper)
  const lower = parseNum(rule.thresholdLower)
  if (upper != null || lower != null) return { upper, lower }

  const single = parseNum(rule.thresholdValue)
  if (single == null) return { upper: null, lower: null }
  const op = String(rule.compareOp || 'gt').toLowerCase()
  if (op === 'lt' || op === 'lte' || op === '<' || op === '<=') {
    return { upper: null, lower: single }
  }
  return { upper: single, lower: null }
}

function ruleAppliesToDevice(rule, deviceType, deviceCode, siteName) {
  const ruleType = (rule.deviceType || '').trim()
  const curType = (deviceType || '').trim()
  if (ruleType && curType && !(ruleType === curType || curType.includes(ruleType) || ruleType.includes(curType))) {
    return false
  }
  const ruleSite = (rule.siteName || '').trim()
  const curSite = (siteName || '').trim()
  if (ruleSite && curSite && !(curSite.includes(ruleSite) || ruleSite.includes(curSite))) {
    return false
  }
  // 绑定了具体设备编号时，必须匹配
  const ruleCode = (rule.deviceCode || '').trim()
  const curCode = (deviceCode || '').trim()
  if (ruleCode) {
    if (!curCode) return false
    return curCode === ruleCode || curCode.includes(ruleCode) || ruleCode.includes(curCode)
  }
  return true
}

function ruleMetricKeys(rule) {
  if (rule.metricKey) return [rule.metricKey]
  const cond = rule.paramCondition || rule.alarmType || ''
  for (let i = 0; i < CONDITION_HINTS.length; i++) {
    if (CONDITION_HINTS[i].re.test(cond)) return CONDITION_HINTS[i].keys
  }
  return CATEGORY_METRICS[rule.alarmCategory] || []
}

/** 指标 → 默认告警类别 */
export const METRIC_ALARM_CATEGORY = {
  stdTotal: 'flow',
  stdInstant: 'flow',
  workTotal: 'flow',
  workInstant: 'flow',
  pressure: 'pressure',
  inletPressure: 'pressure',
  outletPressure: 'pressure',
  outletPressure2: 'pressure',
  outletPressure3: 'pressure',
  diffPressure1: 'pressure',
  diffPressure2: 'pressure',
  temperature: 'temperature',
  leakage: 'water_quality',
  concentration: 'water_quality',
  inletLeak: 'water_quality',
  signalStrength: 'device_status',
  batteryLevel: 'device_status',
  batteryVoltage: 'device_status'
}

/**
 * @returns {'high'|'low'|''} high=超上限红色，low=低于限黄色
 */
export function metricAlarmLevel(value, rules, deviceType, metricKey, deviceCode, siteName) {
  const num = parseNum(value)
  if (num == null || !Array.isArray(rules) || !rules.length) return ''

  let high = false
  let low = false
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]
    if (!rule || String(rule.enabled) === '1') continue
    if (!ruleAppliesToDevice(rule, deviceType, deviceCode, siteName)) continue
    const keys = ruleMetricKeys(rule)
    if (!keys.includes(metricKey)) continue
    const { upper, lower } = ruleBounds(rule)
    if (upper != null && num > upper) high = true
    if (lower != null && num < lower) low = true
  }
  if (high) return 'high'
  if (low) return 'low'
  return ''
}

/** Element class：danger 红 / warn 黄 */
export function metricAlarmClass(value, rules, deviceType, metricKey, deviceCode, siteName) {
  const level = metricAlarmLevel(value, rules, deviceType, metricKey, deviceCode, siteName)
  if (level === 'high') return 'danger'
  if (level === 'low') return 'warn'
  return ''
}

/**
 * 规则是否明确绑定到指定设备。
 * 设备级报警设置只认 device_id 或 device_code 绑定，未绑定设备的全局规则不算，
 * 避免在一个设备的报警设置里修改到全局规则而影响其他站点/设备。
 */
export function ruleBoundToDevice(rule, options = {}) {
  if (!rule) return false
  const ruleId = rule.deviceId
  if (ruleId != null && String(ruleId).trim() !== '') {
    const deviceId = options.deviceId
    if (deviceId == null || String(deviceId).trim() === '') return false
    return String(ruleId).trim() === String(deviceId).trim()
  }
  const ruleCode = String(rule.deviceCode || '').trim()
  const curCode = String(options.deviceCode || '').trim()
  if (ruleCode && curCode) {
    return curCode === ruleCode || curCode.includes(ruleCode) || ruleCode.includes(curCode)
  }
  return false
}

/** 查找与监控指标相关的告警规则 */
export function findMatchingAlarmRules(rules, deviceType, metricKey, options = {}) {
  const enabledOnly = !!options.enabledOnly
  const deviceCode = options.deviceCode || ''
  const siteName = options.siteName || ''
  if (!Array.isArray(rules) || !rules.length || !metricKey) return []
  return rules.filter(rule => {
    if (!rule) return false
    if (enabledOnly && String(rule.enabled) === '1') return false
    if (options.boundOnly) {
      // 设备级报警设置：已经绑定到当前设备的规则直接使用，不再受类型/站点文案差异影响
      if (!ruleBoundToDevice(rule, options)) return false
    } else if (!ruleAppliesToDevice(rule, deviceType, deviceCode, siteName)) {
      return false
    }
    return ruleMetricKeys(rule).includes(metricKey)
  })
}
